import type { InfiniteData, QueryClient, QueryKey } from '@tanstack/react-query';

import type {
    SupportCursorPage,
    SupportMessage,
    SupportRealtimeEvent,
    SupportTicketDetails,
    SupportTicketPage,
    SupportTicketSummary,
} from 'chaika-frontend-types/frontend-cms';

import { supportKeys } from '../queries/supportQueries';
import { matchesTicketListFilter, SupportTicketListFilter } from '../utils/ticketListFilter';

type TicketChange = Partial<Omit<SupportTicketSummary, 'id'>>;

export function applySupportRealtimeEvent(client: QueryClient, event: SupportRealtimeEvent): boolean {
    switch (event.type) {
        case 'message.created': {
            const { ticketId, message } = event.payload;
            upsertMessage(client, ticketId, message);
            updateListedTicket(client, ticketId, ticket => ({
                lastMessageAt:
                    !ticket.lastMessageAt || Date.parse(message.createdAt) > Date.parse(ticket.lastMessageAt)
                        ? message.createdAt
                        : ticket.lastMessageAt,
            }));
            return false;
        }
        case 'message.updated':
            replaceMessage(client, event.payload.ticketId, event.payload.message);
            return false;
        case 'message.deleted':
            removeMessage(client, event.payload.ticketId, event.payload.messageId);
            return false;
        case 'ticket.status_changed': {
            const { ticketId, status, statusName, statusGroup } = event.payload;
            updateTicket(client, ticketId, { status, statusName, statusGroup });
            return false;
        }
        case 'ticket.responsible_changed':
            updateTicket(client, event.payload.ticketId, { responsible: event.payload.responsible });
            return false;
        case 'unread.updated':
            updateListedTicket(client, event.payload.ticketId, () => ({ unread: event.payload.count }));
            client.setQueryData(supportKeys.totalUnread(), event.payload.totalUnread);
            return false;
        case 'ticket.added': {
            const { id, ...addedTicket } = event.payload.ticket;
            updateTicket(client, id, addedTicket);
            insertListedTicket(client, event.payload.ticket);
            return false;
        }
        case 'ticket.removed':
            removeListedTicket(client, event.payload.ticketId);
            client.removeQueries({ queryKey: supportKeys.ticket(event.payload.ticketId) });
            return true;
    }
}

function updateTicket(client: QueryClient, ticketId: string, change: TicketChange): void {
    updateListedTicket(client, ticketId, () => change);
    client.setQueryData<SupportTicketDetails>(supportKeys.ticket(ticketId), current =>
        current ? { ...current, ...pickDetailsChange(change) } : current,
    );
}

function pickDetailsChange({ unread, lastMessageAt, ...change }: TicketChange): Partial<SupportTicketDetails> {
    return change;
}

function updateListedTicket(
    client: QueryClient,
    ticketId: string,
    change: (ticket: SupportTicketSummary) => TicketChange,
): void {
    updateTicketLists(client, (pages, filter) =>
        pages.map(page => ({
            ...page,
            items: page.items.flatMap(ticket => {
                if (ticket.id !== ticketId) return [ticket];
                const updatedTicket = { ...ticket, ...change(ticket) };
                return matchesTicketListFilter(updatedTicket, filter) ? [updatedTicket] : [];
            }),
        })),
    );
}

function insertListedTicket(client: QueryClient, addedTicket: SupportTicketSummary): void {
    updateTicketLists(client, (pages, filter) => {
        const isListed = pages.some(page => page.items.some(ticket => ticket.id === addedTicket.id));
        if (isListed || !matchesTicketListFilter(addedTicket, filter)) return pages;

        return insertBySortOrder(pages, addedTicket);
    });
}

function insertBySortOrder(pages: SupportTicketPage[], addedTicket: SupportTicketSummary): SupportTicketPage[] {
    const pageIndex = pages.findIndex(page => page.items.some(ticket => sortsBefore(addedTicket, ticket)));
    if (pageIndex >= 0) {
        return pages.map((page, index) => {
            if (index !== pageIndex) return page;
            const position = page.items.findIndex(ticket => sortsBefore(addedTicket, ticket));
            return { ...page, items: [...page.items.slice(0, position), addedTicket, ...page.items.slice(position)] };
        });
    }

    const lastPage = pages[pages.length - 1];
    if (!lastPage || lastPage.nextCursor !== null) return pages;

    return [...pages.slice(0, -1), { ...lastPage, items: [...lastPage.items, addedTicket] }];
}

function sortsBefore(ticket: SupportTicketSummary, other: SupportTicketSummary): boolean {
    if (ticket.lastMessageAt === null) return false;
    if (other.lastMessageAt === null) return true;

    return Date.parse(ticket.lastMessageAt) > Date.parse(other.lastMessageAt);
}

function removeListedTicket(client: QueryClient, ticketId: string): void {
    updateTicketLists(client, pages =>
        pages.map(page => ({ ...page, items: page.items.filter(ticket => ticket.id !== ticketId) })),
    );
}

function updateTicketLists(
    client: QueryClient,
    update: (pages: SupportTicketPage[], filter: SupportTicketListFilter) => SupportTicketPage[],
): void {
    client
        .getQueriesData<InfiniteData<SupportTicketPage>>({ queryKey: supportKeys.ticketLists() })
        .forEach(([queryKey, data]) => {
            if (!data) return;
            client.setQueryData<InfiniteData<SupportTicketPage>>(queryKey, {
                ...data,
                pages: update(data.pages, getListFilter(queryKey)),
            });
        });
}

function getListFilter(queryKey: QueryKey): SupportTicketListFilter {
    return queryKey[supportKeys.ticketLists().length] as SupportTicketListFilter;
}

function upsertMessage(client: QueryClient, ticketId: string, message: SupportMessage): void {
    client.setQueryData<InfiniteData<SupportCursorPage<SupportMessage>>>(supportKeys.messages(ticketId), current => {
        if (!current) return current;
        const exists = current.pages.some(page => page.items.some(existing => existing.id === message.id));
        return {
            ...current,
            pages: current.pages.map((page, index) => ({
                ...page,
                items:
                    index === 0 && !exists
                        ? [message, ...page.items]
                        : page.items.map(existing => (existing.id === message.id ? message : existing)),
            })),
        };
    });
}

function replaceMessage(client: QueryClient, ticketId: string, message: SupportMessage): void {
    client.setQueryData<InfiniteData<SupportCursorPage<SupportMessage>>>(supportKeys.messages(ticketId), current =>
        current
            ? {
                  ...current,
                  pages: current.pages.map(page => ({
                      ...page,
                      items: page.items.map(existing => (existing.id === message.id ? message : existing)),
                  })),
              }
            : current,
    );
}

function removeMessage(client: QueryClient, ticketId: string, messageId: string): void {
    client.setQueryData<InfiniteData<SupportCursorPage<SupportMessage>>>(supportKeys.messages(ticketId), current =>
        current
            ? {
                  ...current,
                  pages: current.pages.map(page => ({
                      ...page,
                      items: page.items.filter(message => message.id !== messageId),
                  })),
              }
            : current,
    );
}
